import { throwsNetworkError } from "@/utils/http/NetworkManagerMixin";
import { IAccount } from "@/modules/auth/types";
import { EServices } from "@/types";
import { IVForm } from "@/types";
import JackocoinsNetworkManagerMixin from "@/mixins/JackocoinsNetworkMangerMixin";

import { Component, Inject, Mixins, Ref, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { IAccountClient, IBiometricClient } from "../../types";
import VPasswordField from "@/vuetify-extensions/VPasswordField.vue";
import { requiredLengthRule, requiredRule } from "@/utils/rules";
import { delay } from "@/utils/time";
import { ICustomer } from "@/modules/banks/types";
import { startRegistration } from "@/utils/webauth";

const AccountModule = namespace("AccountModule");

@Component({
  components: {
    VPasswordField,
  },
})
export default class Profile extends Mixins(JackocoinsNetworkManagerMixin) {
  firstName = "";
  lastName = "";
  phoneNumber = "";
  oldPassword = "";
  newPassword = "";
  password = "";
  changingPassword = false;
  firstNameEditable = false;
  lastNameEditable = false;
  phoneNumberEditable = false;
  changePasswordDialogVisible = false;
  confirmDeleteDialogVisible = false;

  @Ref()
  firstNameField!: HTMLInputElement;

  @Ref()
  firstNameForm!: IVForm;

  @Ref()
  lastNameField!: HTMLInputElement;

  @Ref()
  lastNameForm!: IVForm;

  @Ref()
  phoneNumberField!: HTMLInputElement;

  @Ref()
  phoneNumberForm!: IVForm;

  @Ref()
  changePasswordForm!: IVForm;

  @Ref()
  deleteAccountForm!: IVForm;

  @AccountModule.State
  account!: IAccount;

  @AccountModule.State
  customer!: ICustomer;

  @Inject(EServices.account)
  accountClient!: IAccountClient;

  @Inject(EServices.biometric)
  biometricClient!: IBiometricClient;

  requiredRule = requiredRule;
  requiredLengthRule = requiredLengthRule;

  editFirstName() {
    this.firstNameEditable = true;
    delay(100).then(() => this.firstNameField.focus());
  }

  editLastName() {
    this.lastNameEditable = true;
    delay(100).then(() => this.lastNameField.focus());
  }

  editPhoneNumber() {
    this.phoneNumberEditable = true;
    delay(100).then(() => this.phoneNumberField.focus());
  }

  async saveFirstName() {
    await this.updateProfile();
    this.firstNameEditable = false;
  }

  async saveLastName() {
    await this.updateProfile();
    this.lastNameEditable = false;
  }

  async savePhoneNumber() {
    await this.updateCustomer();
    this.phoneNumberEditable = false;
  }

  toggleChangePasswordDialog() {
    this.changePasswordDialogVisible = !this.changePasswordDialogVisible;
  }

  toggleConfirmDeleteDialog() {
    this.confirmDeleteDialogVisible = !this.confirmDeleteDialogVisible;
  }

  @throwsNetworkError()
  async deleteAccount() {
    if (!this.deleteAccountForm.validate()) return;
    await this.accountClient.deleteCustomer(this.password);
    this.$router.replace("/login");
  }

  @throwsNetworkError()
  async updateProfile() {
    await this.accountClient.updateAccount(this.firstName, this.lastName);
    toast({
      icon: "mdi-check",
      iconColor: "green",
      message: "Profile updated",
    });
  }

  @throwsNetworkError()
  async updateCustomer() {
    await this.accountClient.updateCustomer(this.phoneNumber, this.account.id);
    toast({
      icon: "mdi-check",
      iconColor: "green",
      message: "Profile updated",
    });
  }

  @throwsNetworkError()
  async changePassword() {
    if (!this.changePasswordForm.validate()) return;
    this.changingPassword = true;
    await this.accountClient.changePassword(this.oldPassword, this.newPassword);
    this.changingPassword = false;
    toast({
      icon: "mdi-check",
      iconColor: "green",
      message: "Password changed",
    });
    this.changePasswordDialogVisible = false;
  }

  @throwsNetworkError()
  async beginRegistration() {
    try {
      const resp = await this.biometricClient.getRegistrationOptions();
      const opts = resp.data;
      const attResp = await startRegistration(opts);
      const verificationResp = await this.biometricClient.verifyRegisteration(
        attResp
      );
      if (verificationResp && verificationResp.data) {
        toast({
          icon: "mdi-check",
          iconColor: "green",
          message: "Registration succesfull",
        });
      } else {
        toast({
          icon: "mdi-check",
          iconColor: "red",
          message: "Registration unsuccesfull",
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  @Watch("changePasswordDialogVisble")
  async onChangePasswordDialogVisbleChange() {
    if (!this.changePasswordDialogVisible) this.changePasswordForm.reset();
  }

  @Watch("error.changePassword")
  onNetworkError(message: string) {
    if (message)
      toast({
        icon: "mdi-exclamation-thick",
        iconColor: "red",
        message,
      });
    this.changingPassword = !this.changePassword;
  }

  @Watch("error.deleteAccount")
  ondeleteAccountError(message: string) {
    if (message)
      toast({
        icon: "mdi-exclamation-thick",
        iconColor: "red",
        message,
      });
  }

  mounted() {
    this.firstName = this.account.firstName;
    this.lastName = this.account.lastName;
    this.phoneNumber = "";
  }
}
